/**
 * Фільтр «клієнт» у шапці (ч.1075, рішення власника).
 *
 * Якщо в користувача доступ до кількох акаунтів (клієнтів) — стоїть перед
 * організацією за замовчуванням. Показує ВЛАСНИЙ ярлик користувача, якщо
 * його задано, інакше назву: схожі назви клієнтів легко сплутати, тому ярлик
 * («Обліковий запис» → «Мої доступи») користувач задає сам. Вибір — перемикання
 * клієнта (повне перезавантаження, дані різних клієнтів не змішуються); ч.1197:
 * спершу попередження — усі відкриті вкладки закриються, незбережене
 * втратиться (useSwitchTenant). Акаунт, куди зараз не пускає політика власника (2FA / мережа),
 * позначено замком — перемикання відкриє пояснення з діями.
 *
 * Замінює «розумний перемикач» ч.1068 (організації всіх клієнтів в одному
 * списку) і кнопку-іконку клієнта (TenantSwitcher).
 */
import { useQuery } from '@tanstack/react-query';
import { getMyTenants } from '@/api/auth';
import { tenantDisplayName } from '@/auth/tenantLabel';
import { useAuth } from '@/auth/store';
import { useSwitchTenant } from '@/auth/useSwitchTenant';
import { useEffectiveMode, useModeContext } from '@/hooks/useAccountMode';

export default function TenantSelect() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const switchWithWarning = useSwitchTenant();
  const q = useQuery({
    queryKey: ['my-tenants'], queryFn: getMyTenants, staleTime: 5 * 60 * 1000,
  });
  const ctx = useModeContext().data;
  const mode = useEffectiveMode();
  // ч.1405 (рішення власника): у режимі «Користувач» — лише ЧУЖІ акаунти (особистий не показуємо);
  // у «ЕДО» / «Бізнес» — лише власні (і то коли їх кілька: власний акаунт і так зрозумілий).
  const allowed = ctx && mode
    ? new Set((mode === 'USER' ? ctx.foreign_tenants : ctx.own_tenants).map((t) => t.id))
    : null;
  const list = [...(q.data ?? [])]
    .filter((t) => !allowed || allowed.has(t.id))
    .sort((a, b) => tenantDisplayName(a).localeCompare(tenantDisplayName(b), 'uk'));
  // ч.1407 (зауваження власника): у режимі «Користувач» поле чужого акаунта видно завжди — і коли він один
  // (людина має бачити, у чиєму акаунті працює); у власних режимах — лише коли власних акаунтів кілька.
  if (!activeTenantId || list.length < (mode === 'USER' ? 1 : 2)) return null;
  const current = list.find((t) => t.id === activeTenantId);
  const title = current
    ? `Клієнт: ${current.name}${current.label ? ` (ваш ярлик «${current.label}»)` : ''}. `
      + 'Власні ярлики — «Обліковий запис» → «Мої доступи».'
    : 'Клієнт';
  return (
    <div className="app__hf app__hf--tenant">
    <select
      value={activeTenantId}
      aria-label="Клієнт"
      title={title}
      onChange={(e) => {
        const id = e.target.value;
        if (!id || id === activeTenantId) return;
        // Поки користувач не підтвердив — у фільтрі лишається поточний клієнт
        // (значення керує React: після події воно повертається до activeTenantId).
        const target = list.find((t) => t.id === id);
        void switchWithWarning(id, target ? tenantDisplayName(target) : undefined);
      }}
    >
      {list.map((t) => (
        <option key={t.id} value={t.id}>
          {tenantDisplayName(t)}{t.blocked_reason ? ' 🔒' : ''}
        </option>
      ))}
    </select>
    </div>
  );
}
